import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Chip, TextField, MenuItem, InputAdornment, IconButton, Tooltip, Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import { PageHeader } from '../../components/ui/PageHeader';
import { DataTable } from '../../components/common/DataTable';
import { VehicleMap } from '../../components/gps/VehicleMap';
import { gpsAPI, clientAPI } from '../../services/api';
import { useLocale } from '../../contexts/LocaleContext';
import { useToast } from '../../contexts/ToastContext';
import { getPageSubtitle } from '../../constants/pageSubtitles';
import { useSystemTotal } from '../../hooks/useSystemTotal';

interface LiveRow {
  vehicleId: string;
  vehicleNumber: string;
  vehicleType: string;
  clientName: string;
  clientId?: string;
  speed: number;
  movementStatus: string;
  onlineStatus: string;
  address: string;
  latitude?: number;
  longitude?: number;
  lastUpdated?: string;
  hasGps: boolean;
  imeiNumber?: string;
  provider?: string;
}

const hasMapCoordinates = (lat?: number, lng?: number) =>
  lat != null && lng != null && Number.isFinite(lat) && Number.isFinite(lng)
  && !(lat === 0 && lng === 0);

const statusChip = (status: string) => {
  const map: Record<string, { label: string; color: 'success' | 'warning' | 'error' | 'default' | 'info' }> = {
    moving: { label: 'Moving', color: 'success' },
    stopped: { label: 'Stopped', color: 'warning' },
    idle: { label: 'Idle', color: 'info' },
    offline: { label: 'Offline', color: 'default' },
  };
  const cfg = map[status] || { label: status, color: 'default' as const };
  return <Chip size="small" label={cfg.label} color={cfg.color} variant="outlined" />;
};

export const GPSTrackingDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { formatDate } = useLocale();
  const { showToast } = useToast();
  const { total: systemTotal } = useSystemTotal('gps_tracking');
  const [rows, setRows] = useState<LiveRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [clientFilter, setClientFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [clients, setClients] = useState<Array<{ _id: string; clientName: string }>>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [tableSearch, setTableSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const params: Record<string, string> = {};
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      if (clientFilter) params.client = clientFilter;
      if (typeFilter) params.vehicleType = typeFilter;
      const res = await gpsAPI.getLive(params);
      setRows(res.data);
    } catch {
      showToast('Failed to load GPS dashboard', 'error');
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, clientFilter, typeFilter, showToast]);

  useEffect(() => {
    load();
    clientAPI.getAll().then((r) => setClients(r.data || [])).catch(() => {});
  }, [load]);

  useEffect(() => {
    if (!autoRefresh) return undefined;
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [autoRefresh, load]);

  const vehicleTypes = useMemo(
    () => Array.from(new Set(rows.map((r) => r.vehicleType).filter(Boolean))),
    [rows]
  );

  const markers = useMemo(
    () =>
      rows
        .filter((r) => hasMapCoordinates(r.latitude, r.longitude))
        .map((r) => ({
          id: r.vehicleId,
          lat: r.latitude!,
          lng: r.longitude!,
          label: `${r.vehicleNumber} — ${r.speed} km/h`,
          status: r.movementStatus,
          selected: selectedId === r.vehicleId,
        })),
    [rows, selectedId]
  );

  const mapEmptyMessage = useMemo(() => {
    if (loading || markers.length > 0) return undefined;
    const withGps = rows.filter((r) => r.hasGps);
    if (withGps.length === 0) {
      return 'No GPS devices linked yet. Go to Fleet → GPS Devices, add a device, link your vehicle, then send a location update.';
    }
    return 'GPS devices are linked but no location received yet. Use “Send test location” on GPS Devices or configure your tracker webhook.';
  }, [loading, markers.length, rows]);

  const columns = [
    { field: 'vehicleNumber', header: 'Vehicle No.' },
    { field: 'vehicleType', header: 'Type' },
    { field: 'clientName', header: 'Customer' },
    {
      field: 'movementStatus',
      header: 'Status',
      render: (row: Record<string, unknown>) => statusChip(String(row.movementStatus)),
    },
    { field: 'address', header: 'Location' },
    {
      field: 'speed',
      header: 'Speed',
      render: (row: Record<string, unknown>) => `${row.speed ?? 0} km/h`,
    },
    {
      field: 'lastUpdated',
      header: 'Last Update',
      render: (row: Record<string, unknown>) => (row.lastUpdated ? formatDate(String(row.lastUpdated), { dateStyle: 'medium', timeStyle: 'short' }) : '—'),
    },
    {
      field: 'onlineStatus',
      header: 'GPS',
      render: (row: Record<string, unknown>) => (
        <Chip
          size="small"
          label={row.hasGps ? (row.onlineStatus === 'online' ? 'Online' : 'Offline') : 'No Device'}
          color={row.onlineStatus === 'online' ? 'success' : 'default'}
          variant="outlined"
        />
      ),
    },
  ];

  return (
    <Box>
      <PageHeader
        title="Live Map (GPS)"
        subtitle={getPageSubtitle('gps_tracking', 'Live Map', { total: systemTotal })}
        icon={<GpsFixedIcon />}
        action={
          <Tooltip title="Refresh">
            <IconButton onClick={load} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        }
      />

      <VehicleMap
        markers={markers}
        height={380}
        emptyMessage={mapEmptyMessage}
        onMarkerClick={(id) => {
          setSelectedId(id);
          navigate(`/gps/vehicles/${id}`);
        }}
      />

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', my: 2 }}>
        <TextField
          size="small"
          placeholder="Search vehicle number..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ minWidth: 220 }}
        />
        <TextField select size="small" label="Status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} sx={{ minWidth: 140 }}>
          <MenuItem value="">All</MenuItem>
          <MenuItem value="moving">Moving</MenuItem>
          <MenuItem value="stopped">Stopped</MenuItem>
          <MenuItem value="idle">Idle</MenuItem>
          <MenuItem value="offline">Offline</MenuItem>
        </TextField>
        <TextField select size="small" label="Customer" value={clientFilter} onChange={(e) => setClientFilter(e.target.value)} sx={{ minWidth: 180 }}>
          <MenuItem value="">All Clients</MenuItem>
          {clients.map((c) => (
            <MenuItem key={c._id} value={c._id}>{c.clientName}</MenuItem>
          ))}
        </TextField>
        <TextField select size="small" label="Vehicle Type" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} sx={{ minWidth: 160 }}>
          <MenuItem value="">All Types</MenuItem>
          {vehicleTypes.map((t) => (
            <MenuItem key={t} value={t}>{t}</MenuItem>
          ))}
        </TextField>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        {['moving', 'stopped', 'idle', 'offline'].map((s) => {
          const count = rows.filter((r) => r.movementStatus === s).length;
          return (
            <Typography key={s} variant="body2" color="text.secondary">
              {s.charAt(0).toUpperCase() + s.slice(1)}: <strong>{count}</strong>
            </Typography>
          );
        })}
        <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
          Auto-refresh: {autoRefresh ? '30s' : 'off'}{' '}
          <Chip size="small" label={autoRefresh ? 'ON' : 'OFF'} onClick={() => setAutoRefresh((v) => !v)} />
        </Typography>
      </Box>

      <DataTable
        columns={columns}
        rows={rows as unknown as Record<string, unknown>[]}
        loading={loading}
        search={tableSearch}
        onSearchChange={setTableSearch}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={setPage}
        onRowsPerPageChange={setRowsPerPage}
        getRowId={(row) => String(row.vehicleId)}
        onRowClick={(row) => navigate(`/gps/vehicles/${row.vehicleId}`)}
        selectable={false}
        emptyMessage="No vehicles with GPS data. Link GPS devices to vehicles to start tracking."
      />
    </Box>
  );
};
